/**
 * TimePicker Component
 *
 * Modern time selection with quick-select grid and precise wheel selector
 * Uses Radix UI Popover for positioning and accessibility
 *
 * Usage:
 * ```tsx
 * <TimePicker
 *   value="14:30"
 *   onChange={(time) => console.log(time)}
 *   timezone="Europe/Berlin"
 *   timezoneAbbreviation="CET"
 *   selectedDate={new Date()}
 *   allowPast={false}
 * />
 * ```
 */

import { useState, useCallback, useMemo } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { MOBILE_MODAL_POPOVER_COLLISION_PADDING } from '@/components/ui/popoverPlacement';
import { TimePickerProps } from './types';
import { QuickTimeGrid } from './QuickTimeGrid';
import { TimeWheelSelector } from './TimeWheelSelector';

export const TimePicker = ({
  value,
  onChange,
  disabled = false,
  timezone,
  timezoneAbbreviation,
  selectedDate,
  allowPast = false,
  className = '',
}: TimePickerProps) => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const isNarrow = useMediaQuery('(max-width: 639px), (max-height: 500px)');

  // Convert 24-hour time to 12-hour format with AM/PM for display
  const displayTime = useMemo(() => {
    const [hours, minutes] = value.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  }, [value]);

  // Handle time selection from either quick grid or wheel selector
  const handleTimeChange = useCallback(
    (newTime: string) => {
      onChange(newTime);
      // Don't auto-close to allow users to fine-tune with wheel selector
    },
    [onChange]
  );

  return (
    <div className={cn('w-full', className)}>
      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'w-full h-[42px] px-4 rounded-lg border-2 border-gray-300',
              'flex items-center gap-3',
              'focus:outline-none focus:border-primary-500',
              'transition-all duration-200',
              'hover:border-gray-400',
              'bg-white',
              disabled && 'opacity-50 cursor-not-allowed bg-gray-50',
              isOpen && 'border-primary-500'
            )}
            aria-label={t('schedule_time') || 'Select time'}
          >
            <Icon name={ICONS.CLOCK} size="sm" className="text-gray-500" />
            <span className="text-sm text-gray-900 font-medium flex-1 text-left">
              {displayTime}
            </span>
            <svg
              className={cn(
                'w-4 h-4 text-gray-400 transition-transform flex-shrink-0',
                isOpen && 'rotate-180'
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className={cn(
              'bg-white rounded-xl shadow-xl border border-gray-200',
              'p-3 z-popover-top w-[280px] max-w-[calc(100vw-32px)]',
              'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
            )}
            side={isNarrow ? 'bottom' : 'right'}
            sideOffset={8}
            align={isNarrow ? 'end' : 'start'}
            avoidCollisions
            collisionPadding={isNarrow ? MOBILE_MODAL_POPOVER_COLLISION_PADDING : 16}
          >
            <div
              className="overflow-y-auto overscroll-contain p-1"
              style={{ maxHeight: 'max(0px, calc(var(--radix-popover-content-available-height) - 104px))' }}
            >
              {/* Quick Select Grid */}
              <QuickTimeGrid
                selectedTime={value}
                onTimeSelect={handleTimeChange}
                selectedDate={selectedDate}
                timezone={timezone}
                allowPast={allowPast}
                disabled={disabled}
              />

              {/* Divider */}
              <div className="my-2 border-t border-gray-200" />

              {/* Time Wheel Selector */}
              <TimeWheelSelector
                selectedTime={value}
                onTimeChange={handleTimeChange}
                disabled={disabled}
              />

              {/* Timezone Display */}
              {timezone && (
                <>
                  <div className="mt-2 border-t border-gray-200" />
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    <Icon name={ICONS.GLOBE} size="sm" className="text-gray-400" />
                    <span className="text-gray-600">
                      {timezone}
                      {timezoneAbbreviation && (
                        <span className="ml-1 text-gray-500">
                          ({timezoneAbbreviation})
                        </span>
                      )}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Done Button */}
            <div className="mt-3 pt-2 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className={cn(
                  'w-full min-h-[44px] px-3 rounded-lg',
                  'bg-primary-500 text-primary-contrast font-medium text-xs',
                  'hover:bg-primary-600 transition-colors duration-150',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
                )}
              >
                {t('done')}
              </button>
            </div>

            <Popover.Arrow className="fill-white" />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

// Export types
export type { TimePickerProps } from './types';
